import React, {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {WatchPhrase} from "../schemas";
import {useGroupContext} from "./group_context";
import {StylesConfig} from "react-select";
import chroma from "chroma-js";
import {WatchPhraseHighlightColors} from "../colors";

export interface WatchListProviderContextType {
    watchList: WatchPhrase[];
    setWatchList: React.Dispatch<React.SetStateAction<WatchPhrase[]>>;
    /** Add a temporary watch phrase (not persisted to DB) */
    addTemporaryWatchPhrase: (phrase: string) => WatchPhrase;
    /** Remove a temporary watch phrase by its negative ID */
    removeTemporaryWatchPhrase: (id: number) => void;
}

export const WatchListProviderContext = createContext<WatchListProviderContextType | undefined>(undefined);

interface WatchListProviderProps {
    children: ReactNode;
}

export const WatchListProvider: React.FC<WatchListProviderProps> = ({
                                                                        children,
                                                                    }) => {

    const {
        currentGroup,
    } = useGroupContext()!;

    const [watchList, setWatchList] = useState<WatchPhrase[]>([]);
    // Counter for generating unique negative IDs for temporary watch phrases
    const tempIdCounter = useRef(-1);

    useEffect(() => {
        if (currentGroup) {
            setWatchList(currentGroup.watch_phrases);
            // Reset temp counter when group changes
            tempIdCounter.current = -1;
        }
    }, [currentGroup?.id]);

    // Add a temporary watch phrase (uses negative IDs to distinguish from DB entries)
    // Returns existing phrase if it already exists (case-insensitive match)
    const addTemporaryWatchPhrase = useCallback((phrase: string): WatchPhrase => {
        // Check if phrase already exists (case-insensitive)
        const existing = watchList.find(wp => wp.phrase.toLowerCase() === phrase.toLowerCase());
        if (existing) {
            return existing;
        }

        const tempId = tempIdCounter.current--;
        const newWatchPhrase: WatchPhrase = {
            id: tempId,
            phrase: phrase,
            color: WatchPhraseHighlightColors.fromId(tempId),
            group_id: currentGroup?.id || '',
        };
        setWatchList(prev => [...prev, newWatchPhrase]);
        return newWatchPhrase;
    }, [currentGroup?.id, watchList]);

    // Remove a temporary watch phrase by its negative ID
    const removeTemporaryWatchPhrase = useCallback((id: number) => {
        if (id >= 0) return; // Only remove temporary (negative ID) phrases
        setWatchList(prev => prev.filter(wp => wp.id !== id));
    }, []);

    const contextValue: WatchListProviderContextType = {
        watchList,
        setWatchList,
        addTemporaryWatchPhrase,
        removeTemporaryWatchPhrase,
    };

    return (
        <WatchListProviderContext.Provider value={contextValue}>
            {children}
        </WatchListProviderContext.Provider>
    );
};

export const useWatchListContext = () => useContext(WatchListProviderContext);


export interface WatchPhraseOption {
    value: string;
    label: string;
    color: string;
    id?: number;
}

interface ColourOption {
    color: string;
    label: string;
    value: string;
}

export const colourStyles: StylesConfig<ColourOption, true> = {
    control: (styles) => ({...styles, backgroundColor: 'white'}),
    option: (styles, {data, isDisabled, isSelected}) => {
        const color = chroma(data.color);
        return {
            ...styles,
            color: isDisabled
                ? '#ccc'
                : isSelected
                    ? chroma.contrast(color, 'white') > 2
                        ? 'white'
                        : 'black'
                    : undefined,
            cursor: isDisabled ? 'not-allowed' : 'default',

            ':active': {
                ...styles[':active'],
                backgroundColor: !isDisabled
                    ? isSelected
                        ? data.color
                        : color.alpha(0.1).css()
                    : undefined,
            },
        };
    },
    multiValue: (styles, {data}) => {
        // color of the box
        const color = chroma(data.color);
        return {
            ...styles,
            backgroundColor: color.alpha(0.85).css(),
        };
    },
    multiValueLabel: (styles, {data}) => ({
        ...styles,
        color: chroma.contrast(chroma(data.color), 'white') > 2
            ? chroma(data.color).brighten(5.0).css()
            : chroma(data.color).darken(5.0).css(),
    }),
};


export function convertToWatchPhrase(watchPhraseOption: WatchPhraseOption, groupId: string): WatchPhrase {
    return {
        phrase: watchPhraseOption.value,
        id: watchPhraseOption.id!,
        color: watchPhraseOption.color,
        group_id: groupId,
    };
}

export function convertToWatchPhraseOption(watchPhrase: WatchPhrase): WatchPhraseOption {
    return {
        value: watchPhrase.phrase,
        label: watchPhrase.phrase,
        color: watchPhrase.color || WatchPhraseHighlightColors.fromId(),
        id: watchPhrase.id,
    };
}